import { css } from '@emotion/css'
import { Select } from '@mantine/core'
import { PanelDetailsContext } from 'App/Map/PanelDetails/PanelDetails'
import type { TLngLat } from 'App/Map/types'
import { translate } from 'i18n/i18n'
import { useContext, useMemo } from 'react'
import { colors } from 'settings/colors'
import { hubConnectorSettings } from 'settings/hubConnectorSettings'
import { SharedSettings } from 'settings/SharedSettings'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { dropRepeatedSelectValues } from 'utils/dropRepeatedSelectValues'
import { getPointExternalVendorNames } from 'utils/getPointExternalVendorNames'
import type { TSelectItemWithExternalVendors } from 'utils/makeExternalVendorSelectProps'
import { makeExternalVendorSelectProps } from 'utils/makeExternalVendorSelectProps'
import { mantineSelectSearchFilterProp } from 'utils/mantineSelectSearchFilterProp'
import { getAllPointsForFlowMeter } from 'utils/setSelectedFieldAsset/_utils/getArgsForAnchorCoordinates'
import { setSelectedFieldAsset } from 'utils/setSelectedFieldAsset/setSelectedFieldAsset'
import { sortByKey } from 'utils/sortByKey'
import { useSelectClickToggle } from 'utils/useSelectClickToggle'

export const DropdownSelectorFlowMeter = () => {
  const { selectedFlowMeterDevice } = selectedFieldAssetsStore.useSelector((s) => ({
    selectedFlowMeterDevice: s.flowMeterDevice,
  }))

  const { containerWidth } = useContext(PanelDetailsContext)
  const selectClickToggle = useSelectClickToggle()

  const { properties } = fieldAssetStore.useSelector((s) => ({
    properties: s?.properties ?? [],
  }))

  const data = useMemo<{ group: string; items: TSelectItemWithExternalVendors[] }[]>(() => {
    const groups = Object.values(properties ?? {})
      .sort(sortByKey('propertyName'))
      .flatMap((p) => {
        const allPointsWithFlowMeter = getAllPointsForFlowMeter(Object.values(p.points || {}))
        const sortedFlowMeterPoints = allPointsWithFlowMeter.sort(sortByKey('name'))

        if (!sortedFlowMeterPoints.length) return []

        return [
          {
            group: p.propertyName,
            items: sortedFlowMeterPoints.map((point) => {
              return {
                value: point.lngLat,
                label: point.name,
                externalVendorNames: getPointExternalVendorNames({ point, deviceKind: 'flowMeter' }),
              }
            }),
          },
        ]
      })

    return dropRepeatedSelectValues(groups)
  }, [selectedFlowMeterDevice, properties])

  if (!data.length) return null

  const { selectProps, inputPaddingRight } = makeExternalVendorSelectProps({
    data,
    value: selectedFlowMeterDevice,
  })

  return (
    // can prevent the section from collapsing by passing stopPropagation
    <div onClick={(e) => e.stopPropagation()}>
      <Select
        {...selectClickToggle}
        className={css({
          width: containerWidth < 600 ? 200 : Math.min(280, Math.round(containerWidth / 3)),
          border: `1px solid ${colors.grey500}`,
          color: colors.grey800,
          borderRadius: 3,
        })}
        clearable={false}
        data={data}
        {...selectProps}
        filter={mantineSelectSearchFilterProp}
        onChange={(newSelectedFlowMeterDevice) => {
          if (newSelectedFlowMeterDevice === null) return

          setSelectedFieldAsset({
            flowMeterDevice: newSelectedFlowMeterDevice as TLngLat,
          })
        }}
        placeholder={translate.phrases.banyanApp('Select a flow meter device')}
        searchable
        size="xs"
        spellCheck="false"
        styles={{
          ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER,
          dropdown: { marginTop: -6, fontWeight: 'normal' },
          input: {
            ...SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER.input,
            paddingRight: inputPaddingRight,
          },
        }}
        value={selectedFlowMeterDevice}
        comboboxProps={{
          withinPortal: true,
          keepMounted: false,
          zIndex: hubConnectorSettings.zIndexForDropdownSelectorInStackemCharts,
        }}
      />
    </div>
  )
}
